import ReactModal from "react-modal"
import "./ModalDialog.css"
import { ReactComponent as CrossIcon } from "../assets/icons/cross-icon.svg"
import { Fragment, MouseEvent, useMemo, useState } from "react"
import {
  UICell,
  UIColumn,
  UIFlowChartChoiceBlock,
  UILogic,
  UISheet,
  UISheetType,
} from "../types/UITypes"
import { Node } from "reactflow"
import { executeApi } from "../utils/apiUtils"
import { ApiCell, PublishedApi } from "../database/SupabaseConnector"
import {
  comparisonFormulaRegExp,
  comparisonRegExp,
  comparisonShortFormRegExp,
  logicRegExp,
  nestedLogicRegExp,
} from "../utils/constants"
import { getCellValueType } from "../utils/common"

type ApiPreviewItem = {
  sheetId: string
  name: string | undefined
  sheetData:
    | Node<UISheet | UILogic | UIFlowChartChoiceBlock, string | undefined>
    | undefined
  requestPayload: Record<string, any> | undefined
  responseKeys: string[]
}

type ApiTestModalProps = ReactModal.Props & {
  apiPreview: ApiPreviewItem[]
  api: PublishedApi[]
  sheetId?: string
  clusters: Record<string, Node<UISheet | UILogic>[]>
  nodes: Node<UISheet | UILogic | UIFlowChartChoiceBlock>[]
}
type ApiHelperData = {
  cellsBySheetId: Record<string, UICell[]>
  columnsBySheetId: Record<string, UIColumn[]>
  sheetsById: Record<string, UISheet>
  cellsById: Record<string, UICell>
  cells: UICell[]
}

const operators = [
  { value: "eq", label: "=" },
  { value: "gt", label: ">" },
  { value: "ge", label: ">=" },
  { value: "lt", label: "<" },
  { value: "le", label: "<=" },
]

export const ApiTestModal = (props: ApiTestModalProps) => {
  const apiPreviewItem = useMemo(() => {
    return props.apiPreview?.find((item) => item.sheetId === props.sheetId)
  }, [props])

  const apiParams = useMemo(() => {
    const selectedApiPreview = apiPreviewItem as ApiPreviewItem

    if (!selectedApiPreview?.requestPayload) {
      return []
    }

    return Object.keys(selectedApiPreview.requestPayload.inputData)
  }, [apiPreviewItem])

  const [inputs, setInputs] = useState<Record<string, any>>({})

  const handleInputChange = (name: string, value: any) => {
    setInputs((prev) => {
      const updatedInputs = { ...prev, [name]: value }
      if (response) {
        handleTestApi(updatedInputs)
      }

      return updatedInputs
    })
  }

  const [response, setResponse] = useState<string>("")
  const { clusters, sheetId, nodes } = props
  const selectedSheetCluster = clusters[`sheet-${sheetId}` || ""] || []

  const apiHelperData = useMemo(() => {
    const cellsById: Record<string, UICell> = {}
    const cellsBySheetId: Record<string, UICell[]> = {}
    const columnsBySheetId: Record<string, UIColumn[]> = {}
    const cells: UICell[] = []
    const sheets = nodes
      .filter(
        (node): node is Node<UISheet> =>
          (node.data as UISheet).enityType === UISheetType.SHEET
      )
      .map((node) => node.data as UISheet)

    const sheetsById: Record<string, UISheet> = {}

    sheets.forEach((sheet: UISheet) => {
      sheetsById[sheet.id] = sheet
      cellsBySheetId[sheet.id] = []
      columnsBySheetId[sheet.id] = []

      sheet.cells.forEach((cell) => {
        cellsBySheetId[sheet.id].push(cell)
        cellsById[cell.id] = cell
        cells.push(cell)
      })

      sheet.columns?.forEach((column) => {
        columnsBySheetId[sheet.id].push(column)
      })
    })

    return {
      cellsBySheetId,
      columnsBySheetId,
      sheetsById,
      cellsById,
      cells,
    }
  }, [nodes])

  // const apiParamsValues = useMemo(() => {}, [])

  console.log({ clusters, apiHelperData })

  const getDataSheetCellAlias = (name: string | undefined | null) => {
    const [_, alias] = name?.split(":") || []

    return alias
  }

  const getMappedApiCellName = (
    cellId: string,
    apiHelperData: ApiHelperData
  ) => {
    const cellData = apiHelperData.cellsById[cellId]

    const columnLabel = apiHelperData.columnsBySheetId[cellData.sheetId]?.find(
      (columnLabel) => columnLabel.columnIndex === cellData.columnIndex
    )?.text

    // Return api param name based on column label
    return columnLabel
  }

  const conditionToApiCell = (
    name: string,
    mathOperator: string,
    value: string,
    cellData: UICell,
    apiHelperData: ApiHelperData
  ): ApiCell | null => {
    let cellName: string | undefined = name

    if (!cellName) {
      cellName = apiHelperData?.columnsBySheetId[cellData.sheetId].find(
        (columnLabel) => columnLabel.columnIndex === cellData.columnIndex
      )?.text
    }

    const apiCellOperator = operators.find(
      (operator) => operator.label === mathOperator
    )?.value!

    if (!cellName) {
      return null
    }

    return {
      id: "",
      name: cellName,
      operator: apiCellOperator,
      value,
      cellType: 0,
      rowIndex: cellData.rowIndex,
      columnIndex: cellData.columnIndex,
    }
  }

  const getApiCell = (
    cellData: UICell,
    apiHelperData: ApiHelperData
  ): ApiCell => {
    const formulaString = cellData?.name
    const comparisonFormulaRegExpMatch = formulaString?.match(
      comparisonFormulaRegExp
    )
    const logicRegExpMatch = formulaString?.match(logicRegExp)
    const comparisonShortFormRegExpMatch = formulaString?.match(
      comparisonShortFormRegExp
    )
    const cellId = cellData.id

    let apiCellName: string | undefined | null
    let apiCellOperator: string | undefined | null
    let apiCellValue: string | undefined | null

    let cellLogicType: string | undefined
    let cellConditions: ApiCell[] | undefined

    let isLogicExpression = false

    if (logicRegExpMatch) {
      cellLogicType = logicRegExpMatch[2]
      // remove opening and closing bracket
      const logicExpressionsStr = logicRegExpMatch[3]
        .replace("(", "")
        .slice(0, -1)

      const logicExpressions: string[] = []

      const nestedLogicStrings = logicExpressionsStr.split(";")

      const nestedLogics: UICell[] = []

      nestedLogicStrings.forEach((nestedLogicString) => {
        if (nestedLogicString.match(nestedLogicRegExp)) {
          nestedLogics.push({
            ...cellData,
            name: `=${nestedLogicString}`,
          })
        }
      })

      if (nestedLogicStrings.length === nestedLogics.length) {
        const conditions = nestedLogics.map((nestedLogic) =>
          getApiCell(nestedLogic, apiHelperData)
        )

        return {
          id: cellData.id,
          name: apiCellName!,
          operator: apiCellOperator,
          value: apiCellValue,
          cellType: getCellValueType(
            apiCellValue,
            apiHelperData.cells,
            apiCellName
          ),
          cellLogicType,
          conditions,
          rowIndex: cellData.rowIndex,
          columnIndex: cellData.columnIndex,
          sheetId: cellData.sheetId,
        }
      }

      let i = 0
      while (i < logicExpressionsStr.length) {
        let quoteStr = null

        if (logicExpressionsStr.indexOf('"', i) >= 0) {
          i = logicExpressionsStr.indexOf('"', i)
          quoteStr = '"'
        } else if (logicExpressionsStr.indexOf("'", i) >= 0) {
          i = logicExpressionsStr.indexOf("'", i)
          quoteStr = "'"
        }

        const closingQuoteIndex =
          quoteStr && logicExpressionsStr.indexOf(quoteStr, i + 1)

        if (quoteStr && closingQuoteIndex) {
          logicExpressions.push(
            logicExpressionsStr.substring(i + 1, closingQuoteIndex)
          )

          // Move the iteration cursor beyond the closing quote
          i = closingQuoteIndex + 1

          if (i < 0) {
            i = logicExpressionsStr.length
          }
        } else if (i < logicExpressionsStr.length) {
          let substrEnd = logicExpressionsStr.indexOf(",", i)

          if (substrEnd === -1) {
            substrEnd = logicExpressionsStr.length
          }

          const logicExpression = logicExpressionsStr.substring(i, substrEnd)

          logicExpressions.push(logicExpression)

          // Move the iteration cursor beyond the ',' character
          i = substrEnd + 1
        }
      }

      cellConditions = []

      logicExpressions.forEach((logicExpression) => {
        const comparisonRegExpMatch = logicExpression.match(comparisonRegExp)

        if (comparisonRegExpMatch) {
          const apiCell = conditionToApiCell(
            comparisonRegExpMatch[1],
            comparisonRegExpMatch[2],
            comparisonRegExpMatch[3],
            cellData,
            apiHelperData
          )

          if (apiCell) {
            cellConditions!.push(apiCell)
            isLogicExpression = true
          }
        } else {
          // Handle case like: AND|OR("value 1", "value 2", "value 3")
          const apiCell = conditionToApiCell(
            "",
            "=",
            logicExpression,
            cellData,
            apiHelperData
          )

          if (apiCell) {
            cellConditions!.push(apiCell)
            isLogicExpression = true
          }
        }
      })
    }

    if (!isLogicExpression) {
      if (comparisonFormulaRegExpMatch) {
        apiCellName = comparisonFormulaRegExpMatch[2]?.trim()
        const mathOperator = comparisonFormulaRegExpMatch[3]?.trim()
        apiCellValue = comparisonFormulaRegExpMatch[4]?.trim()

        apiCellOperator = operators.find(
          (operator) => operator.label === mathOperator
        )?.value!

        if (!apiCellName && apiCellOperator && apiCellValue) {
          // Use the mapped cell name by default if cell name is not specified
          apiCellName = getMappedApiCellName(cellId!, apiHelperData)
        }
      } else if (comparisonShortFormRegExpMatch) {
        const mathOperator = comparisonShortFormRegExpMatch[1]?.trim()
        apiCellValue = comparisonShortFormRegExpMatch[2]?.trim()

        apiCellOperator = operators.find(
          (operator) => operator.label === mathOperator
        )?.value!

        if (!apiCellName && apiCellOperator && apiCellValue) {
          // Use the mapped cell name by default if cell name is not specified
          apiCellName = getMappedApiCellName(cellId!, apiHelperData)
        }
      } else if (cellData.name) {
        // Use the "=" operator by default if only the mapped cell name and cell name is specified.
        // Also, use cell name as value and sheet name as a cell name in this case.
        apiCellOperator = "eq"
        apiCellValue = cellData.name
        apiCellName = getMappedApiCellName(cellId!, apiHelperData)
      } else if (!apiCellName && !apiCellOperator && !apiCellValue) {
        apiCellName = ""
        apiCellOperator = ""
        apiCellValue = ""
      }
    }

    return {
      id: cellData.id,
      name: apiCellName!,
      operator: apiCellOperator,
      value: apiCellValue,
      cellType: getCellValueType(
        apiCellValue,
        apiHelperData.cells,
        apiCellName
      ),
      cellLogicType,
      conditions: cellConditions,
      rowIndex: cellData.rowIndex,
      columnIndex: cellData.columnIndex,
      sheetId: cellData.sheetId,
    }
  }

  const apiParamValues = useMemo(() => {
    const params: Record<string, any> = {}

    selectedSheetCluster.forEach((node) => {
      if (node.type === "sheet" && node.data.id !== sheetId) {
        const cellsData = apiHelperData.cellsBySheetId[node.data.id]

        cellsData.forEach((cellData) => {
          const apiCell = getApiCell(cellData, apiHelperData)

          if (apiCell) {
            if (!params[apiCell.name]) {
              params[apiCell.name] = []
            }
            params[apiCell.name].push(apiCell.value)
          }
        })
      }
    })
    return params
  }, [selectedSheetCluster, sheetId, apiHelperData])

  const handleTestApi = (inputs: any) => {
    if (!apiPreviewItem) {
      return
    }

    const apiCallResult = executeApi(inputs, apiPreviewItem.sheetId, props.api)

    const response = {
      [apiCallResult?.apiSheet.name || "Response"]: apiCallResult?.values.map(
        (rowCells: any) =>
          rowCells.map((cell: any) => ({
            name: cell.name,
            value: cell.value,
          }))
      ),
    }

    setResponse(JSON.stringify(response, null, 2))
  }

  if (!apiPreviewItem) {
    return null
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 20,
        left: "auto",
        right: 10,
        padding: 10,
        zIndex: 10,
        borderRadius: 4,
        bottom: 20,
        width: 400,
        background: "#fff",
        border: "1px solid #ccc",
      }}
    >
      <div className="modal-dialog-header">
        Test your API
        <CrossIcon
          className="modal-dialog-close-icon"
          onClick={(event) => {
            setResponse("")
            setInputs({})
            props.onRequestClose &&
              props.onRequestClose(event as MouseEvent<SVGSVGElement>)
          }}
        />
      </div>
      <div
        className="modal-dialog-body"
        style={{
          fontSize: 17,
          marginBottom: 20,
          overflowX: "auto",
          maxHeight: "calc(100vh - 220px)",
        }}
      >
        <div
          style={{
            marginBottom: "10px",
            display: "grid",
            gridTemplateColumns: "1fr 6fr",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          {apiParams.map((param) => (
            <Fragment key={param}>
              <span>{param}:</span>
              <div style={{ position: "relative" }}>
                <input
                  key={`api-param-${param}`}
                  value={inputs[param] || ""}
                  onChange={(e) => handleInputChange(param, e.target.value)}
                  style={{
                    width: "100%",
                    maxWidth: 150,
                    height: 24,
                    borderRadius: 4,
                    border: "1px solid grey",
                  }}
                  list={`api-param-options-${param}`}
                  autoComplete="off"
                />
                {Array.isArray(apiParamValues[param]) &&
                  apiParamValues[param].length > 0 && (
                    <datalist id={`api-param-options-${param}`}>
                      {apiParamValues[param].map((option: any, idx: number) => (
                        <option key={idx} value={option} />
                      ))}
                    </datalist>
                  )}
              </div>
            </Fragment>
          ))}
        </div>

        <div style={{ marginTop: 20 }}>Api Response:</div>
        <div>
          <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {response}
          </pre>
        </div>
      </div>
      <div className="modal-dialog-footer" style={{ border: "none" }}>
        <button
          className="blue-button"
          onClick={() => handleTestApi(inputs)}
          style={{ marginTop: 5, marginRight: 10 }}
        >
          Test the API
        </button>
      </div>
    </div>
  )
}
